import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Building2, User, Mail, Phone, MapPin, 
  FileText, Plus, Calendar, Clock, 
  Briefcase, Globe, Edit2, ChevronRight,
  Loader2, ExternalLink, Sparkles, ArrowUpRight
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { auth } from '../utils/auth';
import { useTranslation } from '../context/LanguageContext';
import toast from 'react-hot-toast';
import LexyAppShell from '../components/LexyAppShell';
import CreateCaseModal from '../components/modals/CreateCaseModal';
import { clientsApi, casesApi } from '../services';

// Translations
const pageTranslations = {
  en: {
    corporateClient: 'Corporate client',
    privateClient: 'Private client',
    newCase: 'New case',
    totalCases: 'Total cases',
    activeCases: 'Active cases',
    tasks: 'Tasks',
    clientSince: 'Client since',
    overview: 'Overview',
    cases: 'Cases',
    contacts: 'Contacts',
    contactInfo: 'Contact Information',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    industry: 'Industry',
    website: 'Website',
    notes: 'Notes',
    noCasesRegistered: 'No cases registered',
    createFirstCase: 'Create first case',
    completed: 'Completed',
    inProgress: 'In progress',
    assigned: 'Assigned',
    noContactsRegistered: 'No contact persons registered',
    primaryContact: 'Primary contact',
    actions: 'Actions',
    sendEmail: 'Send email',
    call: 'Call',
    recentActivity: 'Recent Activity',
    lexySuggests: 'Lexy suggests',
    createFirstCaseForClient: 'Create your first case for this client to get started.',
    youHaveActiveCases: 'You have {count} active case(s) with this client.',
    loading: 'Loading...'
  },
  no: {
    corporateClient: 'Bedriftsklient',
    privateClient: 'Privatklient',
    newCase: 'Ny sak',
    totalCases: 'Totalt saker',
    activeCases: 'Aktive saker',
    tasks: 'Oppgaver',
    clientSince: 'Klient siden',
    overview: 'Oversikt',
    cases: 'Saker',
    contacts: 'Kontakter',
    contactInfo: 'Kontaktinformasjon',
    email: 'E-post',
    phone: 'Telefon',
    address: 'Adresse',
    industry: 'Bransje',
    website: 'Nettside',
    notes: 'Notater',
    noCasesRegistered: 'Ingen saker registrert',
    createFirstCase: 'Opprett første sak',
    completed: 'Fullført',
    inProgress: 'Pågår',
    assigned: 'Tildelt',
    noContactsRegistered: 'Ingen kontaktpersoner registrert',
    primaryContact: 'Hovedkontakt',
    actions: 'Handlinger',
    sendEmail: 'Send e-post',
    call: 'Ring',
    recentActivity: 'Siste aktivitet',
    lexySuggests: 'Lexy foreslår',
    createFirstCaseForClient: 'Opprett din første sak for denne klienten for å komme i gang.',
    youHaveActiveCases: 'Du har {count} aktive saker med denne klienten.',
    loading: 'Laster...'
  }
};

interface Contact {
  id: string;
  name: string;
  role?: string;
  email?: string;
  phone?: string;
  is_primary?: boolean;
}

interface ClientData {
  id: string;
  name: string;
  type: 'COMPANY' | 'PERSON';
  company_name?: string;
  person_name?: string;
  org_number?: string;
  email?: string;
  phone?: string;
  address?: string;
  postal_code?: string;
  city?: string;
  industry?: string;
  website?: string;
  notes?: string;
  contacts?: Contact[];
  created_at?: string;
}

interface CaseData {
  id: string;
  topic: string;
  status: string;
  intake_summary?: string;
  updated_at: string;
  created_at: string;
}

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { language } = useTranslation();
  const txt = pageTranslations[language] || pageTranslations.en;
  const [client, setClient] = useState<ClientData | null>(null);
  const [cases, setCases] = useState<CaseData[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'cases' | 'contacts'>('overview');
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  const dateLocale = language === 'no' ? 'no-NO' : 'en-US';

  useEffect(() => {
    if (clientId) {
      fetchClientData();
    }
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      const token = auth.getToken();
      if (!token) {
        navigate('/lexy/login');
        return;
      }

      // Fetch client details
      const clientResponse = await axios.get(`${API_URL}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClient(clientResponse.data);

      // Fetch cases for this client
      try {
        const casesResponse = await axios.get(`${API_URL}/cases?client_id=${clientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCases(casesResponse.data || []);
      } catch (e) {
        console.log('Could not fetch cases');
        setCases([]);
      }
    } catch (error) {
      console.error('Error fetching client:', error);
      toast.error('Kunne ikke laste klient');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (view: string) => {
    navigate(`/lexy/dashboard?tab=${view}`);
  };

  if (loading) {
    return (
      <LexyAppShell activeView="clients" onNavigate={handleNavigate}>
        <div className="min-h-screen bg-white flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      </LexyAppShell>
    );
  }

  if (!client) {
    return (
      <LexyAppShell activeView="clients" onNavigate={handleNavigate}>
        <div className="min-h-screen bg-white flex flex-col items-center justify-center">
          <p className="text-gray-600 mb-4">Klient ikke funnet</p>
          <button 
            onClick={() => navigate('/lexy/dashboard?tab=clients')}
            className="text-gray-900 hover:underline text-sm font-medium"
          >
            Tilbake til klienter
          </button>
        </div>
      </LexyAppShell>
    );
  }

  const isCompany = client.type === 'COMPANY';
  const displayName = isCompany ? (client.company_name || client.name) : (client.person_name || client.name);
  const activeCases = cases.filter(c => c.status === 'IN_PROGRESS' || c.status === 'ASSIGNED').length;

  return (
    <>
    <LexyAppShell activeView="clients" onNavigate={handleNavigate}>
      <div className="min-h-screen bg-white">
        <div className="max-w-6xl mx-auto px-8 py-10">
          
          {/* Header - Minimalist style matching dashboard */}
          <header className="mb-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Back button */}
                <button 
                  onClick={() => navigate('/lexy/dashboard?tab=clients')}
                  className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                
                {/* Client icon */}
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                  isCompany ? 'bg-violet-100' : 'bg-slate-900'
                }`}>
                  {isCompany ? (
                    <Building2 className="w-6 h-6 text-violet-600" />
                  ) : (
                    <span className="text-white font-semibold text-xl">
                      {displayName?.charAt(0)?.toUpperCase() || 'K'}
                    </span>
                  )}
                </div>
                
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
                    {displayName}
                  </h1>
                  <p className="text-gray-500 text-sm">
                    {isCompany ? txt.corporateClient : txt.privateClient}
                    {client.org_number && ` • ${client.org_number}`}
                  </p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowCreateCaseModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-xl hover:bg-slate-800 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {txt.newCase}
                </button>
                <button className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-xl transition-colors">
                  <Edit2 className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>
          </header>

          {/* Stats Row - Clean horizontal layout like dashboard */}
          <div className="flex items-center gap-12 mb-8 pb-8 border-b border-gray-100">
            <div>
              <div className="text-2xl font-semibold text-gray-900">{cases.length}</div>
              <div className="text-xs text-gray-400 mt-0.5">{txt.totalCases}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-emerald-600">{activeCases}</div>
              <div className="text-xs text-gray-400 mt-0.5">{txt.activeCases}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-gray-900">0</div>
              <div className="text-xs text-gray-400 mt-0.5">{txt.tasks}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-gray-900">
                {client.created_at ? new Date(client.created_at).toLocaleDateString(dateLocale, { 
                  month: 'short', 
                  year: 'numeric' 
                }) : '-'}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">{txt.clientSince}</div>
            </div>
          </div>

          {/* Tabs - Minimal pill style */}
          <div className="flex gap-1 mb-8 bg-gray-100 rounded-xl p-1 w-fit">
            {[
              { id: 'overview', label: txt.overview },
              { id: 'cases', label: `${txt.cases} (${cases.length})` },
              ...(isCompany ? [{ id: 'contacts', label: txt.contacts }] : [])
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-3 gap-10">
            
            {/* Left Column - Main Content */}
            <div className="col-span-2">
              
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-8"
                >
                  {/* Contact Information - Clean list style */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
                      {txt.contactInfo}
                    </h3>
                    <div className="space-y-0">
                      {client.email && (
                        <a 
                          href={`mailto:${client.email}`}
                          className="group flex items-center gap-4 py-3 cursor-pointer transition-colors border-b border-gray-50 hover:bg-gray-50 -mx-2 px-2 rounded-lg"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Mail className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-400">{txt.email}</p>
                            <p className="text-sm text-gray-900">{client.email}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-200 group-hover:text-gray-400 transition-colors" />
                        </a>
                      )}
                      
                      {client.phone && (
                        <a 
                          href={`tel:${client.phone}`}
                          className="group flex items-center gap-4 py-3 cursor-pointer transition-colors border-b border-gray-50 hover:bg-gray-50 -mx-2 px-2 rounded-lg"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Phone className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-400">{txt.phone}</p>
                            <p className="text-sm text-gray-900">{client.phone}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-200 group-hover:text-gray-400 transition-colors" />
                        </a>
                      )}
                      
                      {(client.address || client.city) && (
                        <div className="flex items-center gap-4 py-3 border-b border-gray-50">
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                            <MapPin className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-400">{txt.address}</p>
                            <p className="text-sm text-gray-900">
                              {client.address}{client.postal_code && `, ${client.postal_code}`}{client.city && ` ${client.city}`}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {client.industry && (
                        <div className="flex items-center gap-4 py-3 border-b border-gray-50">
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Briefcase className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-400">{txt.industry}</p>
                            <p className="text-sm text-gray-900">{client.industry}</p>
                          </div>
                        </div>
                      )}
                      
                      {client.website && (
                        <a 
                          href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center gap-4 py-3 cursor-pointer transition-colors border-b border-gray-50 hover:bg-gray-50 -mx-2 px-2 rounded-lg"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Globe className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-400">{txt.website}</p>
                            <p className="text-sm text-gray-900">{client.website}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-200 group-hover:text-gray-400 transition-colors" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Notes - Subtle styling */}
                  {client.notes && (
                    <div>
                      <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
                        {txt.notes}
                      </h3>
                      <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{client.notes}</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Cases Tab */}
              {activeTab === 'cases' && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {cases.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <FileText className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-sm mb-4">{txt.noCasesRegistered}</p>
                      <button
                        onClick={() => setShowCreateCaseModal(true)}
                        className="text-sm text-gray-900 font-medium hover:underline inline-flex items-center gap-1"
                      >
                        {txt.createFirstCase} <ArrowUpRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-0">
                      {cases.map((c, index) => (
                        <motion.div
                          key={c.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={() => navigate(`/lexy/case/${c.id}/workspace`)}
                          className="group flex items-center gap-4 py-3 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0"
                        >
                          {/* Status indicator line */}
                          <div className={`w-0.5 h-7 rounded-full flex-shrink-0 ${
                            c.status === 'IN_PROGRESS' ? 'bg-violet-400' :
                            c.status === 'COMPLETED' ? 'bg-gray-300' :
                            'bg-amber-400'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate text-sm group-hover:text-violet-600 transition-colors">
                              {c.topic}
                            </h4>
                            {c.intake_summary && (
                              <p className="text-xs text-gray-400 truncate mt-0.5">{c.intake_summary}</p>
                            )}
                          </div>
                          <span className="text-xs text-gray-400">
                            {c.status === 'COMPLETED' ? txt.completed : 
                             c.status === 'IN_PROGRESS' ? txt.inProgress : txt.assigned}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-200 group-hover:text-gray-400 transition-colors" />
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Contacts Tab */}
              {activeTab === 'contacts' && isCompany && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {(!client.contacts || client.contacts.length === 0) ? (
                    <div className="text-center py-16">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <User className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-sm">{txt.noContactsRegistered}</p>
                    </div>
                  ) : (
                    <div className="space-y-0">
                      {client.contacts.map((contact, index) => (
                        <motion.div
                          key={contact.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="flex items-center gap-4 py-3 border-b border-gray-50 last:border-b-0"
                        >
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {contact.name?.charAt(0)?.toUpperCase() || 'K'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-gray-900 text-sm">{contact.name}</h4>
                              {contact.is_primary && (
                                <span className="px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded text-[10px] font-medium">
                                  {txt.primaryContact}
                                </span>
                              )}
                            </div>
                            {contact.role && (
                              <p className="text-xs text-gray-400">{contact.role}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {contact.email && (
                              <a href={`mailto:${contact.email}`} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                <Mail className="w-4 h-4 text-gray-400" />
                              </a>
                            )}
                            {contact.phone && (
                              <a href={`tel:${contact.phone}`} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                <Phone className="w-4 h-4 text-gray-400" />
                              </a>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-8">
              
              {/* Quick Actions */}
              <div>
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
                  {txt.actions}
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setShowCreateCaseModal(true)}
                    className="w-full px-4 py-3 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    {txt.newCase}
                  </button>
                  {client.email && (
                    <a
                      href={`mailto:${client.email}`}
                      className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      {txt.sendEmail}
                    </a>
                  )}
                  {client.phone && (
                    <a
                      href={`tel:${client.phone}`}
                      className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <Phone className="w-4 h-4" />
                      {txt.call}
                    </a>
                  )}
                </div>
              </div>

              {/* Recent Activity */}
              {cases.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
                    {txt.recentActivity}
                  </h3>
                  <div className="space-y-0">
                    {cases.slice(0, 3).map((c) => (
                      <div 
                        key={c.id} 
                        onClick={() => navigate(`/lexy/case/${c.id}/workspace`)}
                        className="group flex items-start gap-3 py-2.5 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-violet-400 mt-1.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 truncate group-hover:text-violet-600 transition-colors">{c.topic}</p>
                          <p className="text-xs text-gray-400">
                            {new Date(c.updated_at).toLocaleDateString(dateLocale, {
                              day: 'numeric',
                              month: 'short'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Suggestion - matching dashboard style */}
              <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 text-white relative overflow-hidden">
                {/* Subtle reflection */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-violet-400" />
                    <span className="text-xs font-medium text-gray-400">{txt.lexySuggests}</span>
                  </div>
                  <p className="text-sm text-gray-300">
                    {cases.length === 0 
                      ? txt.createFirstCaseForClient
                      : txt.youHaveActiveCases.replace('{count}', String(activeCases))
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LexyAppShell>
    
    {/* Create Case Modal */}
    <CreateCaseModal 
      isOpen={showCreateCaseModal}
      onClose={() => setShowCreateCaseModal(false)}
      preSelectedClientId={clientId}
      onSuccess={(caseId) => {
        setShowCreateCaseModal(false);
        fetchClientData();
      }}
    />
    </>
  );
};

export default ClientDetailPage;
